import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Check if we're on a reel/category page (dark background)
  const isDarkPage =
    location.pathname.startsWith("/category") ||
    location.pathname.startsWith("/project") ||
    location.pathname.startsWith("/jobs");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [mobileMenuOpen]);

  const navLinks = [
    { to: "/", label: "HOME", hash: null },
    { to: "/#vertical-storytelling", label: "VERTICAL STORYTELLING", hash: "vertical-storytelling" },
    { to: "/#advanced-vfx", label: "ADVANCED VFX", hash: "advanced-vfx" },
    { to: "/#cinematic", label: "CINEMATIC", hash: "cinematic" },
    { to: "/jobs", label: "JOBS", hash: null },
    { to: "/#contact", label: "KONTAKT", hash: "contact" },
  ];

  const handleNavClick = (e: React.MouseEvent, hash: string | null) => {
    if (hash) {
      e.preventDefault();

      // If we're on homepage, just scroll
      if (location.pathname === "/") {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } else {
        // Otherwise navigate to homepage with hash
        window.location.href = `/#${hash}`;
      }
    }
  };

  // Dynamic text color based on page type
  const textColor = isDarkPage
    ? "text-white"
    : isScrolled
      ? "text-apple-gray-900"
      : "text-white";

  const hoverColor = isDarkPage
    ? "hover:text-apple-gray-300"
    : isScrolled
      ? "hover:text-apple-gray-700"
      : "hover:text-apple-gray-300";

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300`}
        style={
          isDarkPage
            ? { background: "rgba(0, 0, 0, 0.3)", backdropFilter: "blur(12px)" }
            : isScrolled
              ? { background: "rgba(255, 255, 255, 0.8)", backdropFilter: "blur(16px)", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }
              : { background: "linear-gradient(to bottom, rgba(26, 31, 37, 0.8) 0%, rgba(26, 31, 37, 0.4) 70%, transparent 100%)", backdropFilter: "blur(8px)" }
        }
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo Icon */}
            <Link to="/" className="flex items-center z-50 relative h-8 w-5">
              <img
                src="/images/Logo_ALPHA_solo.png"
                alt="Vertical FX"
                className={`h-8 w-auto absolute transition-opacity duration-300 ${isDarkPage || !isScrolled ? "opacity-100" : "opacity-0"
                  }`}
              />
              <img
                src="/images/Logo_schwarz_solo.png"
                alt="Vertical FX"
                className={`h-8 w-auto absolute transition-opacity duration-300 ${!isDarkPage && isScrolled ? "opacity-100" : "opacity-0"
                  }`}
              />
            </Link>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={(e) => handleNavClick(e, link.hash)}
                  className={`${textColor} ${hoverColor} transition-smooth text-sm font-medium`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden p-2 z-50 ${textColor}`}
              aria-label="Toggle menu"
            >
              <motion.div
                animate={mobileMenuOpen ? "open" : "closed"}
                className="w-6 h-5 flex flex-col justify-between"
              >
                <motion.span
                  variants={{
                    closed: { rotate: 0, y: 0 },
                    open: { rotate: 45, y: 8 },
                  }}
                  className={`w-full h-0.5 ${isDarkPage || !isScrolled ? "bg-white" : "bg-apple-gray-900"} block transition-all origin-center`}
                />
                <motion.span
                  variants={{
                    closed: { opacity: 1 },
                    open: { opacity: 0 },
                  }}
                  className={`w-full h-0.5 ${isDarkPage || !isScrolled ? "bg-white" : "bg-apple-gray-900"} block transition-all`}
                />
                <motion.span
                  variants={{
                    closed: { rotate: 0, y: 0 },
                    open: { rotate: -45, y: -8 },
                  }}
                  className={`w-full h-0.5 ${isDarkPage || !isScrolled ? "bg-white" : "bg-apple-gray-900"} block transition-all origin-center`}
                />
              </motion.div>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-apple-gray-900 z-40 md:hidden"
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="flex flex-col items-center justify-center h-full space-y-8"
            >
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                >
                  <Link
                    to={link.to}
                    onClick={(e) => {
                      handleNavClick(e, link.hash);
                      setMobileMenuOpen(false);
                    }}
                    className="text-white text-3xl font-semibold hover:text-apple-gray-300 transition-colors"
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
